import { useRef } from 'react'
import { Mesh, Vector3 } from 'three'
import { BackgroundImageMaterial } from '~/components/DrawingCanvas/BackgroundImage/components/BackgroundImageMaterial'
import { useBackgroundImage } from '~/components/DrawingCanvas/BackgroundImage/hooks/useBackgroundImage'
import Units from '~/components/DrawingCanvas/lib/units'
import LayerKeys from '~/config/layerKeys'
import { useAppDispatch, useAppSelector } from '~/store/hooks'
import { setCurrentLayer } from '~/store/layers'
import { updateBackgroundImage } from '~/store/objects'
import { showPanel } from '~/store/panel'
import { SELECTED_BACKGROUND_IMAGE_PANEL } from '~/store/panel/types'
import { DragControls, DragControlsProps } from '../../util/DragControls'
import { handleClick } from '~/components/DrawingCanvas/util/eventHandlers'
import { BACKGROUND_Z } from '../../constants/zIndices'
import { getRenderOrder } from '~/components/DrawingCanvas/util/renderOrder'
import { selectObjects } from '~/store/selectedObjects'

export const BackgroundImageMesh = () => {
  const backgroundImage = useBackgroundImage()
  const { width, height, rotation } = backgroundImage
  const { x, y } = backgroundImage.position

  const { visible } = useAppSelector(({ layers }) => layers.layers.BACKGROUND_IMAGE)
  const isMoveTool = useAppSelector(({ tools }) => tools.activeTool === 'MOVE_TOOL')
  const position = useRef(new Vector3(x, y, BACKGROUND_Z))
  const mesh = useRef<Mesh>(null!)

  const dispatch = useAppDispatch()

  const handleDrag: DragControlsProps['onDrag'] = (_event, delta) => {
    if (!isMoveTool) return

    position.current.set(x + delta.x, y + delta.y, BACKGROUND_Z)
    mesh.current.position.copy(position.current)
  }

  const handleDragEnd = () => {
    const [x, y, z] = position.current.toArray()
    dispatch(
      updateBackgroundImage({
        backgroundImage: {
          position: { x, y, z },
          width: Units.nativeToInches(width),
          height: Units.nativeToInches(height),
        },
      })
    )
  }

  return (
    <DragControls
      name={LayerKeys.BACKGROUND_IMAGE}
      dragDisabled={!isMoveTool}
      onDrag={handleDrag}
      onDragEnd={handleDragEnd}
      onTap={handleClick(() => {
        dispatch(selectObjects({ objects: [{ id: backgroundImage.id, className: LayerKeys.BACKGROUND_IMAGE }] }))
        dispatch(setCurrentLayer({ layerKey: LayerKeys.BACKGROUND_IMAGE }))
        dispatch(showPanel({ type: SELECTED_BACKGROUND_IMAGE_PANEL }))
      })}
    >
      <mesh
        renderOrder={getRenderOrder(LayerKeys.BACKGROUND_IMAGE)}
        visible={visible}
        rotation={[rotation.x, rotation.y, (rotation.z * Math.PI) / 180]}
        position={position.current}
        ref={mesh}
      >
        <BackgroundImageMaterial />
        <planeGeometry args={[width, height]} />
      </mesh>
    </DragControls>
  )
}
